import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Radio,
  RadioGroup,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import PaymentsIcon from '@mui/icons-material/Payments';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import InsightsIcon from '@mui/icons-material/Insights';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import FastfoodIcon from '@mui/icons-material/Fastfood';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ContactsIcon from '@mui/icons-material/Contacts';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import DescriptionIcon from '@mui/icons-material/Description';
import { useTranslation } from 'react-i18next';
import salesService, { QuickSaleScreenDto } from '../../../../services/salesService';
import { restaurantScreenApi } from '../../../../services/restaurantApiService';
import accountingService from '../../../../services/accountingService';
import {
  REPORT_SOURCES,
  ReportSpec,
  ReportSpecFilter,
  ReportTimeGranularity,
  ReportVisual,
  TIME_GRANULARITIES,
  createReportId,
  getAllowedVisuals,
  getReportSource,
} from '../reportCatalog';
import DynamicReportCard from './DynamicReportCard';
import GuideButton from '../../../common/guide/GuideButton';

const SOURCE_ICONS: Record<string, React.ReactNode> = {
  quickSaleReceipts: <PointOfSaleIcon color="primary" />,
  receiptPayments: <PaymentsIcon color="primary" />,
  packageOrders: <DeliveryDiningIcon color="primary" />,
  quickSaleItems: <ShoppingBasketIcon color="primary" />,
  restaurantSales: <TableRestaurantIcon color="primary" />,
  restaurantItems: <RestaurantMenuIcon color="primary" />,
  packageOrderItems: <FastfoodIcon color="primary" />,
  cashboxTransactions: <AccountBalanceWalletIcon color="primary" />,
  bankTransactions: <AccountBalanceIcon color="primary" />,
  accountTransactions: <ContactsIcon color="primary" />,
  invoices: <ReceiptLongIcon color="primary" />,
  stockMovements: <Inventory2Icon color="primary" />,
  checkNotes: <DescriptionIcon color="primary" />,
};

const VISUAL_LABEL_KEYS: Record<ReportVisual, string> = {
  kpi: 'dashboard.reportBuilder.visuals.kpi',
  bar: 'dashboard.reportBuilder.visuals.bar',
  pie: 'dashboard.reportBuilder.visuals.pie',
  line: 'dashboard.reportBuilder.visuals.line',
  table: 'dashboard.reportBuilder.visuals.table',
};

interface BranchLike {
  id?: string;
  branchId?: string;
  name?: string;
}

interface ReportBuilderDialogProps {
  open: boolean;
  onClose: () => void;
  /** Kaydet'e basılınca çağrılır (yeni veya düzenlenmiş spec) */
  onSave: (spec: ReportSpec) => void;
  branches: BranchLike[];
  /** Önizleme için dashboard'ın aktif tarih aralığı */
  startDate: string;
  endDate: string;
  /** Düzenleme modu: mevcut spec ile açılır */
  initialSpec?: ReportSpec | null;
}

const STEP_KEYS = [
  'dashboard.reportBuilder.stepSource',
  'dashboard.reportBuilder.stepMetric',
  'dashboard.reportBuilder.stepDimension',
  'dashboard.reportBuilder.stepFilters',
  'dashboard.reportBuilder.stepVisual',
  'dashboard.reportBuilder.stepPreview',
];

/**
 * 6 adımlı rapor kartı sihirbazı:
 * kaynak → metrik → gruplama (+zaman granülaritesi) → filtreler → görsel tip → ad + canlı önizleme.
 * Seçenekler reportCatalog.ts'ten gelir; id'ler backend whitelist'iyle birebir aynıdır.
 */
const ReportBuilderDialog: React.FC<ReportBuilderDialogProps> = ({
  open,
  onClose,
  onSave,
  branches,
  startDate,
  endDate,
  initialSpec,
}) => {
  const { t } = useTranslation('accounting');

  const [activeStep, setActiveStep] = useState(0);
  const [dataSource, setDataSource] = useState('');
  const [metric, setMetric] = useState('');
  const [dimension, setDimension] = useState('none');
  const [timeGranularity, setTimeGranularity] = useState<ReportTimeGranularity>('day');
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  const [visual, setVisual] = useState<ReportVisual>('bar');
  const [title, setTitle] = useState('');

  const [screens, setScreens] = useState<QuickSaleScreenDto[]>([]);
  const [screensLoading, setScreensLoading] = useState(false);
  const [restaurantScreens, setRestaurantScreens] = useState<Array<{ Id: number; Name: string }>>([]);
  const [restaurantScreensLoading, setRestaurantScreensLoading] = useState(false);
  const [cashboxes, setCashboxes] = useState<Array<{ id: string; name: string }>>([]);
  const [cashboxesLoading, setCashboxesLoading] = useState(false);
  const [bankAccounts, setBankAccounts] = useState<Array<{ id: string; name: string }>>([]);
  const [bankAccountsLoading, setBankAccountsLoading] = useState(false);
  const [warehouses, setWarehouses] = useState<Array<{ id: string; name: string }>>([]);
  const [warehousesLoading, setWarehousesLoading] = useState(false);

  // Açılışta formu sıfırla / düzenleme modunda doldur
  useEffect(() => {
    if (!open) return;
    if (initialSpec) {
      setDataSource(initialSpec.dataSource);
      setMetric(initialSpec.metric);
      setDimension(initialSpec.dimension);
      setTimeGranularity(initialSpec.timeGranularity ?? 'day');
      setFilters(Object.fromEntries(initialSpec.filters.map((f) => [f.field, f.values])));
      setVisual(initialSpec.visual);
      setTitle(initialSpec.title);
    } else {
      setDataSource('');
      setMetric('');
      setDimension('none');
      setTimeGranularity('day');
      setFilters({});
      setVisual('bar');
      setTitle('');
    }
    setActiveStep(0);
  }, [open, initialSpec]);

  const source = getReportSource(dataSource);
  const needsScreens = source?.filters.some((f) => f.type === 'screen') ?? false;
  const needsRestaurantScreens = source?.filters.some((f) => f.type === 'restaurantScreen') ?? false;
  const needsCashboxes = source?.filters.some((f) => f.type === 'cashbox') ?? false;
  const needsBankAccounts = source?.filters.some((f) => f.type === 'bankAccount') ?? false;
  const needsWarehouses = source?.filters.some((f) => f.type === 'warehouse') ?? false;

  useEffect(() => {
    if (!open || !needsScreens || screens.length > 0 || screensLoading) return;
    setScreensLoading(true);
    salesService
      .getQuickSaleScreens()
      .then((list) => setScreens(list || []))
      .catch(() => setScreens([]))
      .finally(() => setScreensLoading(false));
  }, [open, needsScreens, screens.length, screensLoading]);

  useEffect(() => {
    if (!open || !needsRestaurantScreens || restaurantScreens.length > 0 || restaurantScreensLoading) return;
    setRestaurantScreensLoading(true);
    restaurantScreenApi
      .getByCompanyId()
      .then((list) => setRestaurantScreens((list || []).filter((s: any) => s.IsActive !== false)))
      .catch(() => setRestaurantScreens([]))
      .finally(() => setRestaurantScreensLoading(false));
  }, [open, needsRestaurantScreens, restaurantScreens.length, restaurantScreensLoading]);

  useEffect(() => {
    if (!open || !needsCashboxes || cashboxes.length > 0 || cashboxesLoading) return;
    setCashboxesLoading(true);
    accountingService
      .getCashboxes()
      .then((list) =>
        setCashboxes(
          (list || [])
            .filter((c) => !c.IsDeleted)
            .map((c) => ({ id: String(c.Id), name: c.Name }))
        )
      )
      .catch(() => setCashboxes([]))
      .finally(() => setCashboxesLoading(false));
  }, [open, needsCashboxes, cashboxes.length, cashboxesLoading]);

  useEffect(() => {
    if (!open || !needsBankAccounts || bankAccounts.length > 0 || bankAccountsLoading) return;
    setBankAccountsLoading(true);
    accountingService
      .getBankAccounts()
      .then((list) =>
        setBankAccounts(
          (list || [])
            .map((a: any) => {
              const id = a.Id ?? a.id;
              const bankName = a.BankName ?? a.bankName ?? '';
              const accountNumber = a.AccountNumber ?? a.accountNumber ?? '';
              return {
                id: id != null ? String(id) : '',
                name: accountNumber ? `${bankName} - ${accountNumber}` : bankName,
              };
            })
            .filter((a) => a.id && a.name)
        )
      )
      .catch(() => setBankAccounts([]))
      .finally(() => setBankAccountsLoading(false));
  }, [open, needsBankAccounts, bankAccounts.length, bankAccountsLoading]);

  useEffect(() => {
    if (!open || !needsWarehouses || warehouses.length > 0 || warehousesLoading) return;
    setWarehousesLoading(true);
    accountingService
      .getWarehouses()
      .then((list) =>
        setWarehouses(
          (list || [])
            .filter((w) => w.IsActive !== false)
            .map((w) => ({ id: String(w.Id), name: w.Name }))
        )
      )
      .catch(() => setWarehouses([]))
      .finally(() => setWarehousesLoading(false));
  }, [open, needsWarehouses, warehouses.length, warehousesLoading]);

  const allowedVisuals = getAllowedVisuals(dimension);

  const buildSpec = (): ReportSpec => ({
    id: initialSpec?.id ?? createReportId(),
    title: title.trim(),
    dataSource,
    metric,
    dimension,
    timeGranularity: dimension === 'time' ? timeGranularity : undefined,
    filters: Object.entries(filters)
      .filter(([, values]) => values.length > 0)
      .map(([field, values]): ReportSpecFilter => ({ field, values })),
    visual,
    limit: 10,
  });

  const previewSpec = useMemo(() => {
    if (!dataSource || !metric) return null;
    return { ...buildSpec(), id: 'custom_preview', title: title.trim() || t('dashboard.reportBuilder.previewTitle') };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, dataSource, metric, dimension, timeGranularity, filters, visual, title]);

  const canProceed = (): boolean => {
    switch (activeStep) {
      case 0: return !!source;
      case 1: return !!metric;
      case 2: return true;
      case 3: return true;
      case 4: return allowedVisuals.includes(visual);
      case 5: return title.trim().length > 0;
      default: return false;
    }
  };

  const handleSelectSource = (sourceId: string) => {
    if (sourceId !== dataSource) {
      setDataSource(sourceId);
      setMetric('');
      setDimension('none');
      setFilters({});
      setVisual('kpi');
    }
  };

  const handleDimensionChange = (value: string) => {
    setDimension(value);
    const allowed = getAllowedVisuals(value);
    if (!allowed.includes(visual)) setVisual(allowed[0]);
  };

  const toggleFilterValue = (field: string, value: string) => {
    setFilters((prev) => {
      const current = prev[field] || [];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, [field]: next };
    });
  };

  const handleSave = () => {
    if (!canProceed()) return;
    onSave(buildSpec());
    onClose();
  };

  const renderStepContent = (): React.ReactNode => {
    switch (activeStep) {
      case 0:
        return (
          <List>
            {REPORT_SOURCES.map((s) => (
              <ListItemButton
                key={s.id}
                selected={dataSource === s.id}
                onClick={() => handleSelectSource(s.id)}
                sx={{ borderRadius: 1, mb: 0.5 }}
              >
                <ListItemIcon>{SOURCE_ICONS[s.id] ?? <InsightsIcon color="primary" />}</ListItemIcon>
                <ListItemText primary={t(s.labelKey)} secondary={t(s.descriptionKey)} />
              </ListItemButton>
            ))}
          </List>
        );

      case 1:
        return (
          <FormControl>
            <RadioGroup value={metric} onChange={(e) => setMetric(e.target.value)}>
              {(source?.metrics ?? []).map((m) => (
                <FormControlLabel key={m.id} value={m.id} control={<Radio size="small" />} label={t(m.labelKey)} />
              ))}
            </RadioGroup>
          </FormControl>
        );

      case 2:
        return (
          <Box>
            <FormControl>
              <RadioGroup value={dimension} onChange={(e) => handleDimensionChange(e.target.value)}>
                <FormControlLabel
                  value="none"
                  control={<Radio size="small" />}
                  label={t('dashboard.reportBuilder.noDimension')}
                />
                {(source?.dimensions ?? []).map((d) => (
                  <FormControlLabel key={d.id} value={d.id} control={<Radio size="small" />} label={t(d.labelKey)} />
                ))}
              </RadioGroup>
            </FormControl>
            {dimension === 'time' && (
              <Box sx={{ mt: 2 }}>
                <FormLabel sx={{ fontSize: '0.85rem' }}>{t('dashboard.reportBuilder.granularityLabel')}</FormLabel>
                <RadioGroup
                  row
                  value={timeGranularity}
                  onChange={(e) => setTimeGranularity(e.target.value as ReportTimeGranularity)}
                >
                  {TIME_GRANULARITIES.map((g) => (
                    <FormControlLabel key={g.id} value={g.id} control={<Radio size="small" />} label={t(g.labelKey)} />
                  ))}
                </RadioGroup>
              </Box>
            )}
          </Box>
        );

      case 3:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="textSecondary">
              {t('dashboard.reportBuilder.filtersHint')}
            </Typography>
            {(source?.filters ?? []).map((f) => {
              const selected = filters[f.id] || [];
              let options: Array<{ value: string; label: string }> = [];
              if (f.type === 'branch') {
                options = branches
                  .filter((b) => (b.id || b.branchId) && b.name)
                  .map((b) => ({ value: (b.id || b.branchId)!, label: b.name! }));
              } else if (f.type === 'screen') {
                options = screens.map((s) => ({ value: String(s.Id), label: s.Name }));
              } else if (f.type === 'restaurantScreen') {
                options = restaurantScreens.map((s) => ({ value: String(s.Id), label: s.Name }));
              } else if (f.type === 'cashbox') {
                options = cashboxes.map((c) => ({ value: c.id, label: c.name }));
              } else if (f.type === 'bankAccount') {
                options = bankAccounts.map((a) => ({ value: a.id, label: a.name }));
              } else if (f.type === 'warehouse') {
                options = warehouses.map((w) => ({ value: w.id, label: w.name }));
              } else {
                options = (f.options || []).map((o) => ({
                  value: o.value,
                  label: o.labelKey ? t(o.labelKey) : o.value,
                }));
              }
              return (
                <Box key={f.id}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    {t(f.labelKey)}
                    {selected.length > 0 && (
                      <Chip size="small" label={selected.length} sx={{ ml: 1, height: 18 }} color="primary" />
                    )}
                  </Typography>
                  {(f.type === 'screen' && screensLoading) ||
                  (f.type === 'restaurantScreen' && restaurantScreensLoading) ||
                  (f.type === 'cashbox' && cashboxesLoading) ||
                  (f.type === 'bankAccount' && bankAccountsLoading) ||
                  (f.type === 'warehouse' && warehousesLoading) ? (
                    <CircularProgress size={18} />
                  ) : options.length === 0 ? (
                    <Typography variant="caption" color="textSecondary">
                      {t('dashboard.reportBuilder.noFilterOptions')}
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {options.map((o) => (
                        <FormControlLabel
                          key={o.value}
                          sx={{ mr: 1 }}
                          control={
                            <Checkbox
                              size="small"
                              checked={selected.includes(o.value)}
                              onChange={() => toggleFilterValue(f.id, o.value)}
                            />
                          }
                          label={<Typography variant="body2">{o.label}</Typography>}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        );

      case 4:
        return (
          <FormControl>
            <RadioGroup value={visual} onChange={(e) => setVisual(e.target.value as ReportVisual)}>
              {allowedVisuals.map((v) => (
                <FormControlLabel key={v} value={v} control={<Radio size="small" />} label={t(VISUAL_LABEL_KEYS[v])} />
              ))}
            </RadioGroup>
          </FormControl>
        );

      case 5:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              label={t('dashboard.reportBuilder.titleLabel')}
              placeholder={t('dashboard.reportBuilder.titlePlaceholder')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              inputProps={{ maxLength: 80 }}
            />
            <Divider />
            <Typography variant="subtitle2">{t('dashboard.reportBuilder.previewLabel')}</Typography>
            {previewSpec && (
              <Box sx={{ height: visual === 'kpi' ? 140 : 300 }}>
                <DynamicReportCard
                  spec={previewSpec}
                  startDate={startDate}
                  endDate={endDate}
                  branches={branches}
                />
              </Box>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InsightsIcon color="primary" />
          <Typography variant="h6" component="span" fontWeight="bold">
            {initialSpec ? t('dashboard.reportBuilder.editTitle') : t('dashboard.reportBuilder.createTitle')}
          </Typography>
          <GuideButton guideKey="finance.reportBuilder" size="small" />
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 2 }}>
          {STEP_KEYS.map((key) => (
            <Step key={key}>
              <StepLabel>{t(key)}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <Box sx={{ minHeight: 220 }}>{renderStepContent()}</Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('dashboard.reportBuilder.cancel')}</Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button disabled={activeStep === 0} onClick={() => setActiveStep((s) => s - 1)}>
          {t('dashboard.reportBuilder.back')}
        </Button>
        {activeStep < STEP_KEYS.length - 1 ? (
          <Button variant="contained" disabled={!canProceed()} onClick={() => setActiveStep((s) => s + 1)}>
            {t('dashboard.reportBuilder.next')}
          </Button>
        ) : (
          <Button variant="contained" disabled={!canProceed()} onClick={handleSave}>
            {t('dashboard.reportBuilder.save')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ReportBuilderDialog;
